import Dexie, { type Table } from 'dexie';
import type { IndicatorInstance } from '@/types/indicator';
import type { DatasetManifest } from '@/lib/lytick/opfs-store';

export interface ChartTemplate {
  id: string;
  name: string;
  createdAt: number;
  chartType: string;
  indicators: IndicatorInstance[];
  timezone: string;
  brickSize?: number;
  styles?: object;
}

export interface ReplayCandleChunk {
  id?: number;
  symbol: string;
  startEpoch: number;
  endEpoch: number;
  granularity: number;
  candles: unknown[];
  fetchedAt: number;
}

export interface ReplayContextChunk {
  id?: number;
  symbol: string;
  startEpoch: number;
  endEpoch: number;
  granularity: number;
  candles: unknown[];
  fetchedAt: number;
}

export interface BootCandleCache {
  key: string;
  symbol: string;
  granularity: number;
  candles: unknown[];
  fetchedAt: number;
}

/**
 * V7 CSV dataset: stores the manifest + optional base buffer for the Dexie fallback.
 * When OPFS is available, `baseBuffer` is omitted (data lives in OPFS files).
 * When OPFS is unavailable, `baseBuffer` holds the serialized CandleColumns.
 */
export interface CsvDataset {
  contentHash: string;       // primary key
  manifest: DatasetManifest;
  baseBuffer?: ArrayBuffer;  // present only in non-OPFS fallback
  savedAt: number;
}

export class LytickDB extends Dexie {
  replayCandleChunks!: Table<ReplayCandleChunk, number>;
  replayContextChunks!: Table<ReplayContextChunk, number>;
  chartTemplates!: Table<ChartTemplate, string>;
  bootCandleCache!: Table<BootCandleCache, string>;
  csvDatasets!: Table<CsvDataset, string>;

  constructor() {
    super('lytick-local-db');

    this.version(1).stores({ replayCandles: '[symbol+timestamp], symbol, timestamp' });
    this.version(2).stores({ replayCandles: null, replayCandleChunks: '++id, [symbol+startEpoch], [symbol+endEpoch], symbol, fetchedAt' });
    this.version(3).stores({ replayContextChunks: '++id, [symbol+startEpoch], symbol, granularity, fetchedAt' });
    this.version(4).stores({ chartTemplates: 'id, name, createdAt, chartType' });
    this.version(5).stores({ bootCandleCache: 'key, symbol, granularity, fetchedAt' });

    // V6: old Candle[]-based storage — now superseded
    this.version(6).stores({ csvDatasets: 'contentHash, savedAt' });

    // V7: manifest + optional binary base buffer (no Candle[] objects)
    this.version(7).stores({ csvDatasets: 'contentHash, savedAt' }).upgrade((tx) => {
      // Migrate: drop old Candle[] rows (they can't be used with the new columnar pipeline)
      return tx.table('csvDatasets').clear();
    });
  }
}

export const db = new LytickDB();

export async function evictStaleCandleChunks() {
  const cutoff = Date.now() - 24 * 3600 * 1000;
  await db.replayCandleChunks.where('fetchedAt').below(cutoff).delete();
  await db.replayContextChunks.where('fetchedAt').below(cutoff).delete();
}
